import { Col, Row } from 'react-bootstrap';

/**
 * Side-by-side JSON diff for the config confirm modals. Highlights every line
 * that belongs to a top-level key whose serialized value differs between the
 * two records — red on the "Current" side (removed / previous value), green on
 * the "Updated" side (added / new value). Nested blocks (apps, groups, budget)
 * are highlighted whole because a line inherits the last depth-1 key above it.
 */

type Obj = Record<string, unknown> | undefined;

const ADDED_BG = 'rgba(25, 135, 84, 0.18)'; // green — added / updated value
const REMOVED_BG = 'rgba(220, 53, 69, 0.16)'; // red — removed / previous value

/** Top-level keys whose serialized value differs between the two records. */
function changedKeys(before: Obj, after: Obj): Set<string> {
  const b = before ?? {};
  const a = after ?? {};
  const changed = new Set<string>();
  for (const k of new Set([...Object.keys(b), ...Object.keys(a)])) {
    if (JSON.stringify(b[k]) !== JSON.stringify(a[k])) changed.add(k);
  }
  return changed;
}

function DiffColumn({ obj, changed, side }: { obj: Obj; changed: Set<string>; side: 'before' | 'after' }) {
  const record = obj ?? {};
  // Sort + drop undefined so both columns line up key-for-key.
  const keys = Object.keys(record)
    .filter((k) => record[k] !== undefined)
    .sort();
  const lines = JSON.stringify(record, keys, 2).split('\n');
  const highlightBg = side === 'before' ? REMOVED_BG : ADDED_BG;
  let currentKey: string | null = null;
  return (
    <pre
      className="bg-light rounded mb-0"
      style={{ maxHeight: '50vh', overflow: 'auto', padding: '0.5rem 0', fontSize: '0.8125rem' }}
    >
      {lines.map((line, i) => {
        // Root braces close the key context; a depth-1 `  "key":` opens one.
        if (line === '{' || line === '}') currentKey = null;
        const keyMatch = line.match(/^ {2}"([^"]+)":/);
        if (keyMatch) currentKey = keyMatch[1];
        const highlighted = currentKey !== null && changed.has(currentKey);
        return (
          <div key={i} style={{ padding: '0 0.75rem', background: highlighted ? highlightBg : 'transparent' }}>
            {line || ' '}
          </div>
        );
      })}
    </pre>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="d-inline-flex align-items-center me-3">
      <span
        style={{ width: 12, height: 12, background: color, borderRadius: 2, display: 'inline-block' }}
        className="me-1"
      />
      <span className="text-muted small">{label}</span>
    </span>
  );
}

export function JsonDiffView({ before, after }: { before: Obj; after: Obj }) {
  const changed = changedKeys(before, after);
  return (
    <>
      <div className="mb-2">
        {changed.size === 0 ? (
          <span className="text-muted small">No differences.</span>
        ) : (
          <>
            <Swatch color={ADDED_BG} label="added / updated" />
            <Swatch color={REMOVED_BG} label="removed / previous" />
          </>
        )}
      </div>
      <Row>
        <Col md={6}>
          <h6 className="text-muted">Current</h6>
          <DiffColumn obj={before} changed={changed} side="before" />
        </Col>
        <Col md={6}>
          <h6 className="text-muted">Updated</h6>
          <DiffColumn obj={after} changed={changed} side="after" />
        </Col>
      </Row>
    </>
  );
}

export default JsonDiffView;
